import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Store, Image as ImageIcon, MessageCircle, User } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { formatRupee as fmtINR } from '@/lib/format';

// Reusable e-commerce style price block: MRP struck through + selling price + discount badge.
// Silently degrades to plain selling price when MRP is missing / <= selling price.
export function PriceDisplay({ mrp, selling, unit, size = 'md' }) {
  const hasDiscount = Number(mrp) > 0 && Number(mrp) > Number(selling);
  const discountPct = hasDiscount ? Math.round(((mrp - selling) / mrp) * 100) : 0;
  const sizes = {
    sm: { price: 'text-sm', mrp: 'text-[10px]', badge: 'text-[9px] px-1 py-0.5' },
    md: { price: 'text-base', mrp: 'text-xs', badge: 'text-[10px] px-1.5 py-0.5' },
    lg: { price: 'text-3xl', mrp: 'text-sm', badge: 'text-xs px-2 py-0.5' },
  };
  const s = sizes[size] || sizes.md;
  return (
    <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5" data-testid="price-display">
      <span className={`${s.price} font-semibold text-amber-800 tabular-nums`}>{fmtINR(selling)}</span>
      {hasDiscount && (
        <>
          <span className={`${s.mrp} text-stone-400 line-through tabular-nums`} data-testid="price-mrp">{fmtINR(mrp)}</span>
          <span className={`${s.badge} inline-flex items-center rounded bg-lime-100 text-lime-800 font-semibold uppercase tracking-wide`} data-testid="discount-badge">
            {discountPct}% OFF
          </span>
        </>
      )}
      {unit && <span className={`${s.mrp} text-stone-400`}>/ {unit}</span>}
    </div>
  );
}

export default function StoreLayout({ children }) {
  return (
    <div className="min-h-screen bg-white text-stone-900 flex flex-col">
      <StoreHeader />
      <main className="flex-1">{children}</main>
      <StoreFooter />
    </div>
  );
}

function StoreHeader() {
  const { totalCount } = useCart();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="store-logo">
          <span className="text-xl font-semibold tracking-tight" style={{ fontFamily: '"Playfair Display", serif' }}>Nirmaan Store</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm">
          <Link to="/shop" className="text-stone-600 hover:text-amber-800 transition" data-testid="nav-shop">Shop</Link>
          <Link to="/portfolio" className="text-stone-600 hover:text-amber-800 transition" data-testid="nav-portfolio">Portfolio</Link>
          <Link to="/get-quote" className="text-stone-600 hover:text-amber-800 transition" data-testid="nav-getquote">Get a Quote</Link>
        </nav>
        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cart"
            onClick={() => navigate('/cart')}
            className="relative p-2 rounded-full hover:bg-stone-100 transition"
            aria-label="Cart"
          >
            <ShoppingCart className="w-5 h-5" />
            {totalCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-amber-700 text-white text-[10px] font-medium rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                {totalCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}

function StoreFooter() {
  return (
    <footer className="mt-16 bg-stone-900 text-stone-300">
      <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <h3 className="text-white font-semibold text-lg mb-2" style={{ fontFamily: '"Playfair Display", serif' }}>Nirmaan Store</h3>
          <p className="text-xs text-stone-400 leading-relaxed">Interior hardware, curated. Delivered straight from trusted vendors — no middleman markup.</p>
        </div>
        <div>
          <h4 className="text-white text-xs uppercase tracking-widest mb-2">Explore</h4>
          <ul className="text-sm space-y-1">
            <li><Link to="/shop" className="hover:text-white">Shop</Link></li>
            <li><Link to="/portfolio" className="hover:text-white">Portfolio</Link></li>
            <li><Link to="/get-quote" className="hover:text-white">Get a Quote</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white text-xs uppercase tracking-widest mb-2">Contact</h4>
          <p className="text-sm">Have a question? Reach us on WhatsApp — we&apos;ll respond within business hours.</p>
        </div>
      </div>
      <div className="border-t border-stone-800 py-4 text-center text-[11px] text-stone-500">
        © {new Date().getFullYear()} Nirmaan Store. All rights reserved.
      </div>
    </footer>
  );
}

export function PlaceholderImage({ className = '' }) {
  return (
    <div className={`flex items-center justify-center bg-stone-100 text-stone-400 ${className}`}>
      <ImageIcon className="w-8 h-8" />
    </div>
  );
}

// Resolve either an absolute URL (https://…) or a relative /api/products/images/… path.
// Relative paths are prefixed with the backend origin so cross-origin dev works.
export function resolveImageSrc(src) {
  if (!src) return null;
  if (src.startsWith('http')) return src;
  return `${process.env.REACT_APP_BACKEND_URL}${src.startsWith('/') ? '' : '/'}${src}`;
}

export function WhatsAppFloat() {
  return (
    <a
      href="https://wa.me/917418086026"
      target="_blank"
      rel="noreferrer"
      className="fixed bottom-6 right-6 z-30 bg-lime-600 hover:bg-lime-700 text-white rounded-full p-3.5 shadow-lg transition"
      data-testid="whatsapp-float"
      aria-label="Chat on WhatsApp"
    >
      <MessageCircle className="w-6 h-6" />
    </a>
  );
}
